import { useMemo } from 'react';
import { useTRPCAPI, useTRPCQuery } from '../../../trpc/trpc';
import { useSMLCountryParam } from './useSMLCountryParam';

export interface TrackAdditionsStats {
  addedToPlaylists: number;
  addedToPlaylistsChange: number | null;
  removedFromPlaylists: number;
  removedFromPlaylistsChange: number | null;
}

export interface TrackAppearancesStats {
  appearedInPlaylists: number;
  appearedInPlaylistsChange: number | null;
}

export interface TrackStats extends TrackAdditionsStats, TrackAppearancesStats {}

interface UseTrackStatsResult {
  /** Additions stats (gain/loss) - available as soon as that query completes */
  additionsStats: TrackAdditionsStats | null;
  /** Appearances stats (visibility) - available as soon as that query completes */
  appearancesStats: TrackAppearancesStats | null;
  /** Combined stats - only available when both queries complete */
  stats: TrackStats | null;
  isAdditionsLoading: boolean;
  isAppearancesLoading: boolean;
  isLoading: boolean;
  error: unknown;
}

/**
 * Hook to fetch track stats (playlist additions, removals, appearances) for a specific date.
 * Uses cached endpoints for optimal performance.
 */
export function useTrackStats(trackId: string | undefined, selectedDate: Date | undefined): UseTrackStatsResult {
  const trpcMusicAPI = useTRPCAPI();
  const { countryCode } = useSMLCountryParam();

  // Fetch playlist additions (gain/loss scores)
  const {
    data: additionsData,
    isLoading: isAdditionsLoading,
    error: additionsError,
  } = useTRPCQuery(
    trpcMusicAPI.tracks.playlistAdditions.queryOptions(
      {
        trackId: trackId ?? '',
        date: selectedDate,
        countryCode,
      },
      {
        enabled: Boolean(trackId),
      }
    )
  );

  // Fetch playlist appearances (visibility score)
  const {
    data: appearancesData,
    isLoading: isAppearancesLoading,
    error: appearancesError,
  } = useTRPCQuery(
    trpcMusicAPI.tracks.playlistAppearances.queryOptions(
      {
        trackId: trackId ?? '',
        date: selectedDate,
        countryCode,
      },
      {
        enabled: Boolean(trackId),
      }
    )
  );

  const additionsStats = useMemo<TrackAdditionsStats | null>(() => {
    if (!additionsData) {
      return null;
    }

    return {
      addedToPlaylists: additionsData.addedToPlaylists.current,
      addedToPlaylistsChange: additionsData.addedToPlaylists.percentageChange,
      removedFromPlaylists: additionsData.removedFromPlaylists.current,
      removedFromPlaylistsChange: additionsData.removedFromPlaylists.percentageChange,
    };
  }, [additionsData]);

  const appearancesStats = useMemo<TrackAppearancesStats | null>(() => {
    if (!appearancesData) {
      return null;
    }

    return {
      appearedInPlaylists: appearancesData.appearedInPlaylists.current,
      appearedInPlaylistsChange: appearancesData.appearedInPlaylists.percentageChange,
    };
  }, [appearancesData]);

  const stats = useMemo<TrackStats | null>(() => {
    if (!additionsStats || !appearancesStats) {
      return null;
    }

    return {
      ...additionsStats,
      ...appearancesStats,
    };
  }, [additionsStats, appearancesStats]);

  return {
    additionsStats,
    appearancesStats,
    stats,
    isAdditionsLoading,
    isAppearancesLoading,
    isLoading: isAdditionsLoading || isAppearancesLoading,
    error: additionsError || appearancesError,
  };
}
